import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { resetPassword, resetPasswordToken } from "../../services/AuthServices";
import Utils from "../../utils";
import useSeo from "../../utils/useSeo";
import "../../asset/style/auth.css";

const STEP = { VALIDATING: 0, FORM: 1, DONE: 2, INVALID: 3 };

const strengthLabel = (pwd) => {
  if (!pwd) return { score: 0, label: "" };
  let score = 0;
  if (pwd.length >= 8) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  const labels = ["Muito fraca", "Fraca", "Razoável", "Forte", "Muito forte"];
  return { score, label: labels[score] };
};

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState(STEP.VALIDATING);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useSeo({
    title: "Definir Nova Palavra-passe",
    description:
      "Crie uma nova palavra-passe segura para a sua conta TP Software. Este link é válido durante um tempo limitado.",
    path: `/reset-password/${token || ""}`,
    noindex: true,
  });

  useEffect(() => {
    if (!token) {
      setStep(STEP.INVALID);
      return;
    }
    resetPasswordToken({ token })
      .then(() => setStep(STEP.FORM))
      .catch(() => setStep(STEP.INVALID));
  }, [token]);

  const strength = strengthLabel(password);

  const submit = (e) => {
    e.preventDefault();
    setError("");
    if (!password) return setError("Palavra-passe é obrigatória");
    if (password.length < 8) return setError("Use pelo menos 8 caracteres");
    if (password !== confirmPassword) return setError("Palavras-passe não coincidem");

    setLoading(true);
    resetPassword({ token, password, confirm_password: confirmPassword })
      .then(() => {
        toast.success("Palavra-passe actualizada!");
        setStep(STEP.DONE);
      })
      .catch((err) => toast.error(Utils.getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  return (
    <div className="tp-auth">
      <div className="tp-auth-left">
        <div className="tp-auth-left-content">
          <img
            src="https://tp.software/libs/images/tpsoftware-green-white.png"
            alt="TP Software"
            className="tp-auth-logo"
          />
          <h1>Uma nova palavra-passe.</h1>
          <p>
            Escolha uma palavra-passe forte para proteger a sua conta TP Software
            e evite reutilizar a mesma noutros sítios.
          </p>
        </div>
      </div>

      <div className="tp-auth-right">
        <div className="tp-auth-card">
          {step === STEP.VALIDATING && (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <div
                style={{
                  width: 44,
                  height: 44,
                  border: "3px solid rgba(139,92,246,0.25)",
                  borderTopColor: "#8B5CF6",
                  borderRadius: "50%",
                  margin: "0 auto 20px",
                  animation: "tp-spin 0.9s linear infinite",
                }}
              />
              <h2>A validar o link…</h2>
              <p className="tp-auth-sub">
                Estamos a confirmar o seu pedido de recuperação.
              </p>
            </div>
          )}

          {step === STEP.INVALID && (
            <div style={{ textAlign: "center" }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: "50%",
                  background: "rgba(239,68,68,0.12)",
                  color: "#EF4444",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 30,
                  margin: "0 auto 20px",
                  border: "1px solid rgba(239,68,68,0.3)",
                }}
              >
                ⚠
              </div>
              <h2>Link inválido ou expirado</h2>
              <p className="tp-auth-sub">
                O link de recuperação já não é válido. Peça um novo email de
                recuperação para continuar.
              </p>
              <Link
                to="/forgot-password"
                className="tp-auth-submit"
                style={{
                  display: "block",
                  textAlign: "center",
                  textDecoration: "none",
                }}
              >
                Pedir novo link
              </Link>
              <div className="tp-auth-footer">
                <Link to="/login">← Voltar ao login</Link>
              </div>
            </div>
          )}

          {step === STEP.FORM && (
            <>
              <h2>Nova palavra-passe</h2>
              <p className="tp-auth-sub">
                Escolha uma palavra-passe com pelo menos 8 caracteres.
              </p>
              <form onSubmit={submit}>
                <div className="tp-auth-field">
                  <label>Nova palavra-passe</label>
                  <input
                    type="password"
                    className="tp-auth-input"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoFocus
                    autoComplete="new-password"
                  />
                  {password && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        marginTop: 8,
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          height: 6,
                          background: "#242A31",
                          borderRadius: 3,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            width: `${(strength.score / 4) * 100}%`,
                            height: "100%",
                            background:
                              strength.score <= 1
                                ? "#EF4444"
                                : strength.score === 2
                                ? "#F59E0B"
                                : strength.score === 3
                                ? "#A78BFA"
                                : "#10B981",
                            transition: "width .25s, background .25s",
                          }}
                        />
                      </div>
                      <span style={{ fontSize: 12, color: "#8E98A6" }}>
                        {strength.label}
                      </span>
                    </div>
                  )}
                </div>
                <div className="tp-auth-field">
                  <label>Confirmar palavra-passe</label>
                  <input
                    type="password"
                    className={`tp-auth-input${error ? " error" : ""}`}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                  {error && <span className="tp-auth-error">{error}</span>}
                </div>
                <button
                  type="submit"
                  className="tp-auth-submit"
                  disabled={loading}
                >
                  {loading ? "A gravar..." : "Alterar palavra-passe"}
                </button>
              </form>
            </>
          )}

          {step === STEP.DONE && (
            <div className="tp-auth-success">
              <div className="tp-auth-success-icon">✓</div>
              <h2>Tudo pronto!</h2>
              <p className="tp-auth-sub">
                A sua palavra-passe foi actualizada com sucesso. Pode iniciar
                sessão.
              </p>
              <button
                type="button"
                className="tp-auth-submit"
                onClick={() => navigate("/login")}
              >
                Entrar na conta
              </button>
            </div>
          )}

          <div className="tp-auth-footer">
            <Link to="/login">← Voltar ao login</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
